import type { ChannelMessage, StreamEvent } from "../blackboardModels";
import type { IngestedStreamEvent } from "../useInstanceStream";

export interface TimelineIdentity {
  avatarSeed: string;
  displayName: string;
  id: string | null;
  source: "known-actor" | "fallback" | "system" | "user";
}

export interface TimelineMessageEntry {
  id: string;
  identity: TimelineIdentity;
  isLive: boolean;
  isTurnEnd: boolean;
  kind: "message";
  label: string;
  message: ChannelMessage;
  orderKey: string;
  timestamp: number;
}

export interface TimelineActivityEntry {
  event: IngestedStreamEvent;
  id: string;
  identity: TimelineIdentity | null;
  isLive: boolean;
  isTurnEnd: boolean;
  kind: "activity";
  label: string;
  orderKey: string;
  timestamp: number;
}

export interface TimelineBoundaryEntry {
  id: string;
  identity: null;
  isLive: true;
  isTurnEnd: false;
  kind: "live-boundary";
  label: string;
  orderKey: string;
  timestamp: number;
}

export type TimelineEntry =
  | TimelineActivityEntry
  | TimelineBoundaryEntry
  | TimelineMessageEntry;

export interface TimelineActorAttribution {
  avatarSeed: string;
  displayName: string;
  id: string;
}

export interface SubmitTimelineMessageInput {
  appendLocalUserMessage: (message: ChannelMessage) => void;
  channelId: string;
  draft: string;
  makeLocalTimestamp?: () => string;
  onSendMessage: (channelId: string, message: string) => Promise<string>;
}

export interface SubmitTimelineMessageResult {
  draft: string;
  error: string | null;
  sent: boolean;
}

const LIVE_BOUNDARY_LABEL = "live telemetry begins here";

function shortDisplayId(id: string | null | undefined): string {
  const trimmed = id?.trim();
  return trimmed ? trimmed.slice(0, 8) : "former member";
}

function identityFromSender(input: {
  displayName?: string | null;
  id: string | null;
  attributionById: ReadonlyMap<string, TimelineActorAttribution>;
  source: "instance" | "system" | "user";
}): TimelineIdentity {
  if (input.source === "system") {
    return {
      avatarSeed: "system",
      displayName: "system",
      id: null,
      source: "system",
    };
  }

  const attribution = input.id
    ? input.attributionById.get(input.id)
    : undefined;
  if (attribution) {
    return {
      avatarSeed: attribution.avatarSeed,
      displayName: attribution.displayName,
      id: attribution.id,
      source: "known-actor",
    };
  }

  const displayName = input.displayName?.trim() || shortDisplayId(input.id);
  return {
    avatarSeed: input.id ?? displayName,
    displayName,
    id: input.id,
    source: input.source === "user" ? "user" : "fallback",
  };
}

export function activityLabel(event: StreamEvent): string | null {
  switch (event.type) {
    case "channel_message": {
      return null;
    }
    case "tool_call": {
      return event.tool;
    }
    case "phase_transition": {
      return `${event.from} → ${event.to}`;
    }
    case "status_change": {
      return `status: ${event.status}`;
    }
    case "budget_warning": {
      return event.message;
    }
    case "cache_stats": {
      const hit =
        event.cacheHitPct !== null
          ? `${event.cacheHitPct}% hit`
          : `read:${event.cacheReadTokens}`;
      return `cache ${hit}${event.isBreak ? " [break]" : ""}`;
    }
    case "token_delta": {
      return `in:${event.inputTokensDelta} out:${event.outputTokensDelta} $${event.costDeltaUsd.toFixed(4)}`;
    }
    case "activity_signal": {
      if (event.label === "WAKE") {
        return `woke ← ${event.message}`;
      }
      if (event.label === "PARK") {
        return `parked ← ${event.message}`;
      }
      if (event.label === "LOCK") {
        return event.message
          .replace(/^waiting for #.*$/u, "lock waiting")
          .replace(/^acquired after /u, "lock acquired after ");
      }
      return `[${event.label}] ${event.message}`;
    }
    default: {
      return null;
    }
  }
}

function compareTimelineEntries(
  left: TimelineEntry,
  right: TimelineEntry,
): number {
  const timestampDelta = left.timestamp - right.timestamp;
  if (timestampDelta !== 0) {
    return timestampDelta;
  }
  return left.orderKey.localeCompare(right.orderKey);
}

function markTurnEnds(entries: TimelineEntry[]): TimelineEntry[] {
  // Waiting-dedup is tracked PER ACTOR: with concurrent actors interleaving,
  // a global flag resets on every other actor's event and re-marks each
  // repeated `status: waiting` as a turn end, shattering capsules.
  const waitingBoundaryConsumed = new Map<string | null, boolean>();
  return entries.map((entry) => {
    if (entry.kind !== "activity") {
      return { ...entry, isTurnEnd: false };
    }
    const owner = entry.identity?.id ?? null;

    if (
      entry.event.type === "activity_signal" &&
      entry.event.label === "PARK"
    ) {
      waitingBoundaryConsumed.set(owner, false);
      return { ...entry, isTurnEnd: true };
    }

    if (
      entry.event.type === "status_change" &&
      entry.event.status === "waiting"
    ) {
      const isTurnEnd = !waitingBoundaryConsumed.get(owner);
      waitingBoundaryConsumed.set(owner, true);
      return { ...entry, isTurnEnd };
    }

    if (
      entry.event.type === "status_change" &&
      entry.event.status === "killed"
    ) {
      waitingBoundaryConsumed.set(owner, false);
      return { ...entry, isTurnEnd: true };
    }

    waitingBoundaryConsumed.set(owner, false);
    return { ...entry, isTurnEnd: false };
  });
}

export function buildTimelineEntries(input: {
  actorAttributions: TimelineActorAttribution[];
  events: IngestedStreamEvent[];
  liveSince: string | null;
  messages: ChannelMessage[];
}): TimelineEntry[] {
  const liveSinceMs = input.liveSince ? Date.parse(input.liveSince) : null;
  const attributionById = new Map(
    input.actorAttributions.map((attribution) => [attribution.id, attribution]),
  );
  const messageEntries: TimelineMessageEntry[] = input.messages.map(
    (message) => {
      const timestamp = new Date(message.createdAt).getTime();
      return {
        id: `message:${message.id}`,
        identity: identityFromSender({
          attributionById,
          displayName: message.senderDisplayName,
          id: message.senderId,
          source: message.senderType,
        }),
        isLive: liveSinceMs !== null && timestamp >= liveSinceMs,
        isTurnEnd: false,
        kind: "message",
        label: message.content,
        message,
        orderKey: `message:${message.id}`,
        timestamp,
      };
    },
  );
  const activityEntries: TimelineActivityEntry[] = input.events.flatMap(
    (event) => {
      const label = activityLabel(event);
      if (label === null) {
        return [];
      }
      const timestamp = new Date(event.timestamp).getTime();
      return [
        {
          event,
          id: `activity:${event.ingestionSeq}:${event.id}`,
          identity: event.sourceInstanceId
            ? identityFromSender({
                attributionById,
                id: event.sourceInstanceId,
                source: "instance",
              })
            : null,
          isLive: true,
          isTurnEnd: false,
          kind: "activity",
          label,
          orderKey: `activity:${String(event.ingestionSeq).padStart(12, "0")}:${event.id}`,
          timestamp,
        },
      ];
    },
  );
  const boundaryEntry: TimelineBoundaryEntry[] =
    liveSinceMs === null
      ? []
      : [
          {
            id: `live-boundary:${input.liveSince}`,
            identity: null,
            isLive: true,
            isTurnEnd: false,
            kind: "live-boundary",
            label: LIVE_BOUNDARY_LABEL,
            orderKey: "boundary",
            timestamp: liveSinceMs,
          },
        ];

  return markTurnEnds(
    [...messageEntries, ...boundaryEntry, ...activityEntries].toSorted(
      compareTimelineEntries,
    ),
  );
}

export async function submitTimelineMessage(
  input: SubmitTimelineMessageInput,
): Promise<SubmitTimelineMessageResult> {
  const trimmed = input.draft.trim();
  if (!trimmed) {
    return { draft: input.draft, error: null, sent: false };
  }

  try {
    const messageId = await input.onSendMessage(input.channelId, trimmed);
    input.appendLocalUserMessage({
      channelId: input.channelId,
      content: trimmed,
      createdAt: input.makeLocalTimestamp?.() ?? new Date().toISOString(),
      id: messageId,
      senderDisplayName: "You",
      senderId: "you",
      senderType: "user",
    });
    return { draft: "", error: null, sent: true };
  } catch {
    return {
      draft: input.draft,
      error: "Message failed to send. Your draft was restored.",
      sent: false,
    };
  }
}
